import { Injectable } from '@angular/core';
import {
  Observable,
  defer,
  firstValueFrom,
  concatMap,
  filter,
  finalize,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  toArray,
} from 'rxjs';
import {
  CreateGroupSessionModel,
  GroupSessionMemberModel,
  GroupSessionModel,
  PatientSessionModel,
} from '../api/models';
import {
  OfflineDataService,
  OfflineGroupSessionModel,
  PatientOfflineData,
} from './offline-data.service';
import { StaffApiService } from '../api/services';
import '../../shared/utils/utils';

@Injectable({ providedIn: 'root' })
export class GroupSessionService {
  private reconciliationInFlight: Observable<boolean> | null = null;
  private activeReconciliations = 0;

  constructor(
    private staffApiService: StaffApiService,
    private offlineDataService: OfflineDataService
  ) {}

  getGroupSession(
    groupSessionID: number
  ): Observable<OfflineGroupSessionModel> {
    const cached =
      this.offlineDataService.getOfflineGroupSession(groupSessionID);
    if (
      cached &&
      (groupSessionID < 0 || this.offlineDataService.isInOfflineMode())
    ) {
      return of(cached);
    }

    if (this.offlineDataService.isInOfflineMode()) {
      return this.offlineDataService
        .getOfflineGroupSessionsObservable()
        .pipe(
          map((groups) => groups.get(groupSessionID) ?? null),
          filter((group): group is OfflineGroupSessionModel => !!group),
          take(1)
        );
    }

    return this.staffApiService
      .GetGroupSession({ groupSessionID })
      .pipe(
        switchMap((group) => this.cacheGroupAndPatients(group)),
        map(() => this.offlineDataService.getOfflineGroupSession(groupSessionID)!)
      );
  }

  createGroupSession(
    patientIDs: number[],
    sparkModeId: number,
    serviceID?: number | null
  ): Observable<OfflineGroupSessionModel> {
    const distinctPatientIDs = Array.from(new Set(patientIDs));
    if (distinctPatientIDs.length < 2) {
      throw new Error('Select at least two clients');
    }

    if (this.offlineDataService.isInOfflineMode()) {
      return from(
        this.createOfflineGroup(distinctPatientIDs, sparkModeId, serviceID)
      );
    }

    const body: CreateGroupSessionModel = {
      ClientOperationID: this.createClientOperationID('online-group'),
      PatientIDs: distinctPatientIDs,
      SparkModeID: sparkModeId,
      ServiceID: serviceID ?? undefined,
    };
    return this.staffApiService
      .CreateGroupSession({ body })
      .pipe(switchMap((group) => this.cacheGroupAndPatients(group)));
  }

  reconcilePendingGroups(): Observable<boolean> {
    if (this.offlineDataService.isInOfflineMode()) {
      return of(false);
    }
    if (this.reconciliationInFlight) {
      return this.reconciliationInFlight;
    }

    const pending = this.offlineDataService
      .getOfflineGroupSessions()
      .filter((group) => (group.ID ?? 0) < 0 && !!group.isDirty);
    if (pending.length === 0) {
      return of(true);
    }

    const reconciliation = defer(async () => {
      this.activeReconciliations++;
      // Wait for every group, including promise-based ID/signature remapping.
      // forkJoin would unsubscribe siblings on the first error while their writes continue.
      const results = await Promise.allSettled(pending.map(async group => {
        const clientOperationID = group.ClientOperationID ?? this.createClientOperationID('offline-group');
        group.ClientOperationID = clientOperationID;
        await this.offlineDataService.saveOfflineGroupSession(group);
        const serverGroup = await firstValueFrom(this.staffApiService.CreateGroupSession({
          body: {
            ClientOperationID: clientOperationID,
            CreatedDate: group.CreatedDate,
            PatientIDs: this.members(group).map(member => member.PatientID)
              .filter((id): id is number => id != null),
            SparkModeID: this.getGroupSparkModeId(group),
            ServiceID: group.ServiceID ?? undefined,
          } satisfies CreateGroupSessionModel,
        }));
        await this.reconcileGroup(group, serverGroup);
      }));
      const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (failure) throw failure.reason;
      return true;
    }).pipe(
      finalize(() => {
        this.activeReconciliations--;
        if (this.reconciliationInFlight === reconciliation) this.reconciliationInFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.reconciliationInFlight = reconciliation;
    return reconciliation;
  }

  hasActiveReconciliation(): boolean {
    return this.activeReconciliations > 0;
  }

  private cacheGroupAndPatients(
    group: GroupSessionModel
  ): Observable<OfflineGroupSessionModel> {
    const members = this.members(group);
    const patientLoads =
      members.length > 0
        ? from(members).pipe(
            concatMap((member) => {
              const patientID = member.PatientID;
              if (patientID == null) {
                return of(false);
              }
              const sessionID = member.SessionID;
              return this.offlineDataService.savePatientForOffline(
                patientID,
                false,
                sessionID != null && sessionID > 0 ? sessionID : undefined
              );
            }),
            toArray()
          )
        : of([]);
    return patientLoads.pipe(
      switchMap(() =>
        from(
          this.offlineDataService.saveOfflineGroupSession({
            ...group,
            isDirty: false,
          })
        )
      ),
      map(() => group)
    );
  }

  private async createOfflineGroup(
    patientIDs: number[],
    sparkModeId: number,
    serviceID?: number | null
  ): Promise<OfflineGroupSessionModel> {
    const temporaryGroupID = -Date.now();
    const createdDate = new Date().toServerString();
    const members: GroupSessionMemberModel[] = [];
    const patientDataByID = new Map<number, PatientOfflineData>();

    for (const patientID of patientIDs) {
      const patientData =
        this.offlineDataService.getOfflinePatientData(patientID);
      if (!patientData) {
        throw new Error(
          `Client ${patientID} is not available offline. Download the client before creating the group.`
        );
      }
      patientDataByID.set(patientID, patientData);
    }

    for (let index = 0; index < patientIDs.length; index++) {
      const patientID = patientIDs[index];
      const patientData = patientDataByID.get(patientID)!;
      const sessionID = temporaryGroupID - index - 1;
      const session: PatientSessionModel & { isDirty?: boolean } = {
        ID: sessionID,
        PatientID: patientID,
        GroupSessionID: temporaryGroupID,
        GroupSessionCreatedDate: createdDate,
        SparkModeID: sparkModeId,
        ServiceID: serviceID ?? undefined,
        TargetData: [],
        BehaviorData: [],
      };
      await this.offlineDataService.saveOfflineSessionData(
        patientID,
        session,
        true,
        false
      );
      members.push({
        PatientID: patientID,
        PatientName: this.getPatientName(patientData.patient),
        SessionID: sessionID,
      });
    }

    const group: OfflineGroupSessionModel = {
      ClientOperationID: this.createClientOperationID('offline-group'),
      ID: temporaryGroupID,
      ServiceID: serviceID ?? undefined,
      SparkModeID: sparkModeId,
      CreatedDate: createdDate,
      Members: members,
      isDirty: true,
    };
    await this.offlineDataService.saveOfflineGroupSession(group);
    return group;
  }

  private async reconcileGroup(
    localGroup: OfflineGroupSessionModel,
    serverGroup: GroupSessionModel
  ): Promise<void> {
    const memberMappings = this.members(localGroup).map((localMember) => {
      const serverMember = this.members(serverGroup).find(
        (member) => member.PatientID === localMember.PatientID
      );
      const patientID = localMember.PatientID;
      const localSessionID = localMember.SessionID;
      const patientData =
        patientID == null
          ? null
          : this.offlineDataService.getOfflinePatientData(patientID);
      const localSession = patientData?.sessions.find(
        (session) => session.ID === localSessionID
      );
      if (
        !serverMember ||
        !localSession ||
        patientID == null ||
        localSessionID == null ||
        serverMember.SessionID == null ||
        serverMember.PatientID == null
      ) {
        throw new Error(
          `Unable to reconcile group member for client ${localMember.PatientID}`
        );
      }
      return {
        localMember,
        serverMember,
        localSession,
        patientID,
        localSessionID,
        serverSessionID: serverMember.SessionID,
      };
    });

    for (const {
      localMember,
      serverMember,
      localSession,
      patientID,
      localSessionID,
      serverSessionID,
    } of memberMappings) {
      const reconciledSession: PatientSessionModel = {
        ...localSession,
        ID: serverSessionID,
        GroupSessionID: serverGroup.ID,
        GroupSessionCreatedDate: serverGroup.CreatedDate,
        TargetData: localSession.TargetData?.map((data) => ({
          ...data,
          PatientSessionID: serverSessionID,
        })),
        BehaviorData: localSession.BehaviorData?.map((data) => ({
          ...data,
          PatientSessionID: serverSessionID,
        })),
      };
      await this.offlineDataService.rekeyOfflineSessionArtifacts(
        patientID,
        localSessionID,
        serverSessionID
      );
      this.rekeyRunningTimers(localSessionID, serverSessionID);
      await this.offlineDataService.removeOfflineSessionData(
        patientID,
        localSessionID
      );
      await this.offlineDataService.saveOfflineSessionData(
        patientID,
        reconciledSession,
        true,
        false
      );
      localMember.SessionID = serverSessionID;
      localMember.StartedDate = reconciledSession.StartedDate;
      localMember.FinishedDate = reconciledSession.FinishedDate;
    }

    const temporaryGroupID = localGroup.ID;
    if (temporaryGroupID == null) {
      throw new Error('Unable to reconcile group session without an ID');
    }
    localGroup.ID = serverGroup.ID;
    localGroup.ServiceID = serverGroup.ServiceID;
    localGroup.CreatedDate = serverGroup.CreatedDate;
    localGroup.CreatedByUserID = serverGroup.CreatedByUserID;
    localGroup.isDirty = false;
    await this.offlineDataService.replaceOfflineGroupSession(
      temporaryGroupID,
      localGroup
    );

    if (window.location.pathname.includes(`/staff/group/${temporaryGroupID}`)) {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname.replace(
          `/staff/group/${temporaryGroupID}`,
          `/staff/group/${serverGroup.ID}`
        )}${window.location.search}`
      );
    }
  }

  private members(group: GroupSessionModel): GroupSessionMemberModel[] {
    return group.Members ?? [];
  }

  private getPatientName(patient: {
    FirstName?: string | null;
    LastName?: string | null;
    PreferredName?: string | null;
  }): string {
    return (
      patient.PreferredName ||
      [patient.FirstName, patient.LastName].filter(Boolean).join(' ') ||
      'Client'
    );
  }

  private createClientOperationID(prefix: string): string {
    const randomID =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${prefix}:${randomID}`;
  }

  private getGroupSparkModeId(group: GroupSessionModel): number {
    if (group.SparkModeID != null) {
      return group.SparkModeID;
    }

    for (const member of this.members(group)) {
      if (member.PatientID == null) {
        continue;
      }
      const patientData = this.offlineDataService.getOfflinePatientData(
        member.PatientID
      );
      const session = patientData?.sessions.find(
        (item) => item.ID === member.SessionID
      );
      if (session?.SparkModeID != null) {
        return session.SparkModeID;
      }
    }
    return 0;
  }

  private rekeyRunningTimers(
    oldSessionID: number,
    newSessionID: number
  ): void {
    const storedData = localStorage.getItem('targetsRunningData');
    if (storedData) {
      const runningData = JSON.parse(storedData) as {
        SessionID: number;
        TargetID: number;
        Data: unknown;
      }[];
      runningData.forEach((item) => {
        if (item.SessionID === oldSessionID) {
          item.SessionID = newSessionID;
        }
      });
      localStorage.setItem('targetsRunningData', JSON.stringify(runningData));
    }
    window.dispatchEvent(
      new CustomEvent('sparkSessionIDReconciled', {
        detail: { oldSessionID, newSessionID },
      })
    );
  }
}
